'use client';

import { css } from '@bts-web/utils-style-engine';
import { FC, ReactElement, ReactNode } from 'react';
import { useScrollLock } from '../../utils/useScrollLock';
import { HTMLRenderer } from '../HTMLRenderer/HTMLRenderer';
import { easeInOut, motion } from 'framer-motion';
import { RegularArrowLeft, RegularClose } from '../../svgIcons';

type DialogCommonBaseProps = {
  id: string;
  visual?: 'primary' | 'secondary';
  title?: string;
  size: 'small' | 'fullscreen' | 'medium';
  onClose?: () => void;
  backAction?: () => void;
  onMobileFullScreen?: boolean;
  showHeaderBottomDivider?: boolean;
  contentProps?: {
    disableContentPadding?: boolean;
    disableTitlePadding?: boolean;
    disableTitleGap?: boolean;
  };
  smallTitle?: boolean;
  disableCloseButton?: boolean;
  children: ReactNode;
  className?: string;
  topIcon?: ReactNode;
};

type DialogBaseProps =
  | (DialogCommonBaseProps & {
      aboveTitleElement?: ReactNode;
      belowTitleElement?: ReactElement;
      showTitleBelowHeader: boolean;
    })
  | (DialogCommonBaseProps & {
      aboveTitleElement?: never;
      belowTitleElement?: never;
      showTitleBelowHeader?: never;
    });

const modalHeaderLateralElementWidth = css({
  width: '16px',
});

const modalVariants = {
  initial: { scale: 0.8, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  exit: { scale: 0.8, opacity: 0 },
};

const modalTransition = {
  duration: 0.2,
  ease: easeInOut,
};

const DialogBase: FC<DialogBaseProps> = ({
  size,
  visual = 'primary',
  children,
  id,
  onClose,
  title,
  aboveTitleElement = null,
  belowTitleElement = null,
  backAction,
  showHeaderBottomDivider = false,
  onMobileFullScreen,
  smallTitle = false,
  disableCloseButton = false,
  contentProps = {},
  showTitleBelowHeader = false,
  className,
  topIcon,
}) => {
  const hasBackButton = !!backAction;

  const showHeader = hasBackButton || title || !!disableCloseButton === false;

  useScrollLock(true);

  const titleElement = (
    <div
      data-testid="dialog-title"
      data-element="dialog-title"
      style={{ display: !!title === false ? 'none' : 'block' }}
      className={css({
        px: { base: 'small', lg: 'medium' },
        ...(!contentProps?.disableTitlePadding
          ? {
              py: { base: 'extra_small' },
            }
          : {}),
        color: 'neutrals.text_primary',
        fontSize: {
          lg: 'body.medium_medium',
          base: 'body.small_medium',
        },
        fontWeight: 'body.medium_medium',
        letterSpacing: 'body.medium_medium',
        lineHeight: {
          lg: 'body.medium_medium',
          base: 'body.small_medium',
        },
        width: '100%',
        textAlign: 'center',
        ...(smallTitle
          ? {}
          : {
              fontSize: 'headline.large_semi_bold',
              fontWeight: 'headline.large_semi_bold',
              lineHeight: 'headline.large_semi_bold',
              letterSpacing: 'headline.large_semi_bold',
            }),
      })}
    >
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        })}
      >
        {topIcon}
        {title && <HTMLRenderer>{title}</HTMLRenderer>}
      </div>
    </div>
  );

  const titlePlaceholder = (
    <div
      data-testid="dialog-title-placeholder"
      data-element="dialog-title-placeholder"
      className={css({
        px: { base: 'small', lg: 'medium' },
        flex: 1,
        textAlign: 'center',
      })}
    />
  );

  return (
    <motion.div
      id={id}
      title={title}
      data-testid={id ? `dialog-base-${id}` : 'dialog-base'}
      data-element={`modal-${id}`}
      initial="initial"
      animate="animate"
      exit="exit"
      variants={modalVariants}
      transition={modalTransition}
      className={`${css({
        display: 'flex',
        flexDirection: 'column',
        boxShadow: `0px 0px 8px 0px rgba(0, 0, 0, 0.15)`,
        borderEndEndRadius: 'largeBottomRight',
        borderEndStartRadius: 'largeTopRight',
        borderStartStartRadius: 'largeTopLeft',
        borderStartEndRadius: 'largeBottomLeft',
        backfaceVisibility: 'hidden',
        overflow: 'hidden',
        m: 'auto',
        ...(visual === 'primary'
          ? {
              backgroundColor: 'neutrals.fill_quinary',
            }
          : {}),
        ...(visual === 'secondary'
          ? {
              backgroundColor: 'neutrals.card_fill_primary',
            }
          : {}),
        ...(size === 'fullscreen'
          ? {
              width: '100vw',
              height: '100vh',
            }
          : {}),
        ...(size === 'small'
          ? {
              width: '90vw',
              maxWidth: '614px',
              maxHeight: '98vh',
            }
          : {}),
        ...(size === 'medium'
          ? {
              width: '90vw',
              maxWidth: '1000px',
              maxHeight: '98vh',
            }
          : {}),
        ...(onMobileFullScreen
          ? {
              textAlign: 'center',
              flex: 'auto',
              width: { base: '100%' },
              height: { base: '100vh', lg: 'auto' },
              maxWidth:
                size === 'medium'
                  ? {
                      base: '100%',
                      lg: '1000px',
                    }
                  : {
                      base: '100%',
                      lg: '614px',
                    },
              maxHeight: { base: '100%', lg: '98vh' },
              overflow: 'auto',
              backgroundColor: 'screen_background.primary',
              borderEndEndRadius: {
                base: 0,
                md: 'largeBottomRight',
              },
              borderEndStartRadius: {
                base: 0,
                md: 'largeTopRight',
              },
              borderStartStartRadius: {
                base: 0,
                md: 'largeTopLeft',
              },
              borderStartEndRadius: {
                base: 0,
                md: 'largeBottomLeft',
              },
            }
          : {}),
      })} ${className}`}
    >
      {showHeader ? (
        <div
          data-element="dialog-header"
          className={css({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            ...(showHeaderBottomDivider
              ? {
                  width: 'auto',
                  borderBottomWidth: '1px',
                  borderBottomColor: 'neutrals.divider',
                }
              : {}),
            px: {
              base: 'small',
              lg: 'medium',
            },
            py: {
              base: 'small',
            },
          })}
        >
          {hasBackButton ? (
            <button
              type="button"
              data-element="dialog-back-btn"
              data-testid="dialog-back-btn"
              aria-label="Back"
              onClick={backAction}
              className={modalHeaderLateralElementWidth}
            >
              <RegularArrowLeft size={24} data-dir="rtl" />
            </button>
          ) : (
            <div
              data-testid="filler element"
              className={modalHeaderLateralElementWidth}
            />
          )}

          {showTitleBelowHeader ? titlePlaceholder : titleElement}

          {!disableCloseButton ? (
            <button
              type="button"
              style={{ cursor: 'pointer' }}
              data-element="dialog-close-btn"
              data-testid="dialog-close-btn"
              aria-label="Close"
              onClick={() => onClose?.()}
              className={`${css({
                ml: 'auto',
                cursor: 'pointer',
                color: 'neutrals.text_primary',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'small',
              })} ${modalHeaderLateralElementWidth}`}
            >
              <RegularClose size={16} />
            </button>
          ) : (
            <div
              data-testid="filler element"
              className={modalHeaderLateralElementWidth}
            />
          )}
        </div>
      ) : null}

      <div
        id="dialog-content"
        data-element="dialog-content"
        data-testid="dialog-content"
        className={css({
          display: 'flex',
          flexDirection: 'column',
          flex: '1',
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          backfaceVisibility: 'hidden',
          ...(!contentProps?.disableContentPadding
            ? {
                px: {
                  base: 'large',
                  lg: 'extra_large',
                },
                py: 'medium',
              }
            : {}),
        })}
      >
        {showTitleBelowHeader &&
          (title || aboveTitleElement || belowTitleElement) && (
            <div
              data-element="title-below-header-container"
              className={css({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flexShrink: 0,
                ...(!contentProps?.disableTitleGap
                  ? {
                      gap: { base: 'medium' },
                    }
                  : {}),
              })}
            >
              {aboveTitleElement}
              {title && titleElement}
              {belowTitleElement}
            </div>
          )}

        {children}
      </div>
    </motion.div>
  );
};

export { DialogBase };
